/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
  TemplateRef
} from '@angular/core';

import { HorizontalPosition, PhxHandleStyle, PhxSliderShowTooltip, PhxSliderHandler } from './phx-slider.types';
import { PhxTooltipDirective } from '../tooltip/phx-tooltip.directive';
import { PhxTooltipSize } from '../tooltip/phx-tooltip.component';
import { PHX_SIZE } from '../../constants';
import mixinDisabled from '../../mixins/disabled';

const baseMixin = mixinDisabled(
  class {
    constructor() {}
  }
);

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  selector: 'phx-slider-handle',
  templateUrl: './phx-handle.component.html',
  inputs: [
    'disabled'
  ],
})
export class PhxSliderHandleComponent extends baseMixin implements OnChanges {
  static ngAcceptInputType_active: Boolean;

  @ViewChild('handle', { static: false }) handleEl?: ElementRef;
  @ViewChild(PhxTooltipDirective, { static: false }) tooltip?: PhxTooltipDirective;

  @Input() phxOffset?: number;
  @Input() phxValue?: number;
  @Input() phxTooltipVisible: PhxSliderShowTooltip = 'hover';
  @Input() phxTooltipFormatter?: null | ((value: number) => string);
  @Input() phxTooltipContent?: string | TemplateRef<PhxSliderHandler>;
  @Input() phxTooltipSize?: PhxTooltipSize = PHX_SIZE.SM;
  @Input() phxActive: boolean = false;
  @Input() disabled: boolean = false;

  tooltipContent?: string;
  phxStyle: PhxHandleStyle = {left: '', right: '', transform: ''};

  constructor(private cdr: ChangeDetectorRef) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { phxOffset, phxValue, phxDir, disabled, phxTooltipVisible } = changes;

    if (phxOffset || phxDir) {
      this.updateStyle();
    }

    if (phxValue) {
      this.updateTooltipTitle();
      this.updateTooltipPosition();
    }

    if (disabled) {
      this.cdr.detectChanges();
    }

    if (phxTooltipVisible) this.cdr.detectChanges();
  }

  phxFocus(e): void {
    this.handleEl?.nativeElement.focus();
  }

  public get tooltipContextData(): PhxSliderHandler {
    return {
      value: this.phxValue,
      offset: this.phxOffset,
      active: this.phxActive,
    };
  }

  private updateTooltipTitle(): void {
    this.tooltipContent = this.phxTooltipFormatter ? this.phxTooltipFormatter(this.phxValue) : `${this.phxValue}`;
  }

  private updateTooltipPosition(): void {
    if (this.tooltip) {
      this.cdr.detectChanges();
      this.tooltip?.updateToggleTooltipPosition();
    }
  }

  private updateStyle(): void {
    const positionStyle: PhxHandleStyle = {
          ...this.getHorizontalStylePosition(),
          transform: `translateX('-50%')`
        };

    this.phxStyle = positionStyle;
    this.cdr.markForCheck();
  }

  private getHorizontalStylePosition(): HorizontalPosition {
    const left = `${this.phxOffset}%`;
    const right = 'auto';
    return { left, right };
  }
}